import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";
import IntegrationActivityLog from "../IntegrationActivityLog";
import { PrimaryBlueButton, SecondaryBlueButton } from "@velaro/velaro-component-lib";

interface WebflowConfig {
  id: number;
  siteId: number;
  displayName: string;
  webflowSiteId: string;
  webflowSiteName: string;
  webflowWorkspaceId: string;
  scriptRegistered: boolean;
  pushFormSubmissions: boolean;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export default function Webflow() {
  const api = useApi();
  const navigate = useNavigate();
  const [configs, setConfigs] = useState<WebflowConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const resp = await api.admin.get("Webflow/Configs");
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setConfigs(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(`Failed to load Webflow connections: ${String(err)}`);
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  useEffect(() => {
    load();
  }, [load]);

  const handleConnect = async () => {
    try {
      const resp = await api.admin.get("Webflow/OAuth/AuthUrl");
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      window.location.href = data.url;
    } catch (err) {
      setError(`Failed to start Webflow connection: ${String(err)}`);
    }
  };

  const handleInjectWidget = async (config: WebflowConfig) => {
    setActionLoading(config.id);
    try {
      const resp = await api.admin.post(`Webflow/Configs/${config.id}/InjectWidget`, {});
      if (!resp.ok) {
        const body = await resp.text();
        setError(`Widget injection failed: ${body}`);
        return;
      }
      await load();
    } catch (err) {
      setError(`Widget injection error: ${String(err)}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemoveWidget = async (config: WebflowConfig) => {
    if (!confirm(`Remove the Velaro widget from "${config.webflowSiteName}"?`)) return;
    setActionLoading(config.id);
    try {
      const resp = await api.admin.post(`Webflow/Configs/${config.id}/RemoveWidget`, {});
      if (!resp.ok) {
        const body = await resp.text();
        setError(`Widget removal failed: ${body}`);
        return;
      }
      await load();
    } catch (err) {
      setError(`Widget removal error: ${String(err)}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (config: WebflowConfig) => {
    if (!confirm(`Disconnect "${config.webflowSiteName}"? This will not remove the widget from your site.`)) return;
    try {
      const resp = await api.admin.delete(`Webflow/Configs/${config.id}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setConfigs(prev => prev.filter(c => c.id !== config.id));
    } catch (err) {
      setError(`Failed to disconnect: ${String(err)}`);
    }
  };

  const handleToggleEnabled = async (config: WebflowConfig) => {
    try {
      const resp = await api.admin.put(`Webflow/Configs/${config.id}`, { enabled: !config.enabled });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      await load();
    } catch (err) {
      setError(`Failed to update: ${String(err)}`);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="integration-page-wrapper">
      {error && (
        <div className="error-banner" style={{ marginBottom: 16, padding: "12px 16px", background: "#FEE2E2", borderRadius: 8, color: "#991B1B" }}>
          {error}
          <button onClick={() => setError(null)} style={{ marginLeft: 12, fontWeight: 600 }}>✕</button>
        </div>
      )}

      <div className="integration-header" style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <img src="/img/webflow.svg" alt="Webflow" style={{ width: 40, height: 40 }} />
          <div>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Webflow</h2>
            <p style={{ margin: 0, color: "var(--color-text-secondary)", fontSize: 14 }}>
              Connect your Webflow site and inject the Velaro chat widget with one click.
            </p>
          </div>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <PrimaryBlueButton label="+ Connect Webflow Site" onClick={handleConnect} />
        </div>
      </div>

      {configs.length === 0 ? (
        <div className="empty-state" style={{ textAlign: "center", padding: "48px 24px", background: "var(--color-surface)", borderRadius: 12 }}>
          <img src="/img/webflow.svg" alt="" style={{ width: 56, height: 56, marginBottom: 16, opacity: 0.4 }} />
          <h3 style={{ margin: "0 0 8px" }}>No Webflow sites connected</h3>
          <p style={{ color: "var(--color-text-secondary)", marginBottom: 24 }}>
            Connect a Webflow site to inject the Velaro chat widget and monitor form submissions.
          </p>
          <PrimaryBlueButton label="Connect Webflow Site" onClick={handleConnect} />
        </div>
      ) : (
        <div className="configs-list" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {configs.map(config => (
            <div
              key={config.id}
              style={{
                background: "var(--color-surface)",
                border: "1px solid var(--color-border)",
                borderRadius: 12,
                padding: "20px 24px",
                display: "flex",
                alignItems: "center",
                gap: 20
              }}
            >
              <img src="/img/webflow.svg" alt="" style={{ width: 32, height: 32, flexShrink: 0 }} />

              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{config.webflowSiteName}</div>
                <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginTop: 2 }}>
                  Site ID: {config.webflowSiteId}
                </div>
                <div style={{ marginTop: 8, display: "flex", gap: 8, alignItems: "center" }}>
                  {config.scriptRegistered ? (
                    <span style={{ fontSize: 12, background: "#DCFCE7", color: "#166534", padding: "2px 8px", borderRadius: 999, fontWeight: 600 }}>
                      Widget Active
                    </span>
                  ) : (
                    <span style={{ fontSize: 12, background: "#FEF9C3", color: "#854D0E", padding: "2px 8px", borderRadius: 999, fontWeight: 600 }}>
                      Widget Not Injected
                    </span>
                  )}
                  {!config.enabled && (
                    <span style={{ fontSize: 12, background: "#F3F4F6", color: "#6B7280", padding: "2px 8px", borderRadius: 999 }}>
                      Disabled
                    </span>
                  )}
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
                {actionLoading === config.id ? (
                  <LoadingSpinner inline />
                ) : config.scriptRegistered ? (
                  <SecondaryBlueButton
                    label="Remove Widget"
                    onClick={() => handleRemoveWidget(config)}
                  />
                ) : (
                  <PrimaryBlueButton
                    label="Inject Widget"
                    onClick={() => handleInjectWidget(config)}
                  />
                )}
                <SecondaryBlueButton
                  label={config.enabled ? "Disable" : "Enable"}
                  onClick={() => handleToggleEnabled(config)}
                />
                <SecondaryBlueButton
                  label="Disconnect"
                  onClick={() => handleDelete(config)}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 40 }}>
        <IntegrationActivityLog integrationSlug="Webflow" />
      </div>
    </div>
  );
}
